import React, { useEffect } from "react";
import {
  Facebook, Instagram, Twitter, Youtube,
  Copyright, Smartphone, MapPin, Star,
  Globe, ArrowRight, ShoppingBag,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import AppleLogo   from "../assets/app/apple.svg";
import GoogleLogo  from "../assets/app/play.svg";
import StripeImage from "../assets/app/stripe.png";
import logoImg     from "../assets/app/logo.png";
import { fetchSocialLinks } from "../redux/socialSlice";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────────────────
   THEME — METARELM BLUE/PURPLE/PINK
───────────────────────────────────────────────────────── */
const C = {
  bg:       "#07050f",
  surface:  "#0d0a1a",
  surface2: "#120f20",
  blue:     "#007BFF",
  purple:   "#6A00FF",
  pink:     "#FF00D4",
  white:    "#ffffff",
  offwhite: "#e8e8e8",
  muted:    "#6b6880",
  border:   "#1a1626",
  border2:  "#231e35",
  sans:     "'Plus Jakarta Sans', sans-serif",
};

/* Metarelm brand gradient */
const MR_G      = "linear-gradient(90deg, #007BFF 0%, #6A00FF 50%, #FF00D4 100%)";
const MR_G_ANIM = "linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4, #6A00FF, #007BFF)";

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');
  *,*::before,*::after{box-sizing:border-box;}

  @keyframes mrSpin{
    0%  {background-position:0% 50%;}
    50% {background-position:100% 50%;}
    100%{background-position:0% 50%;}
  }
  @keyframes shimmer{
    0%  {background-position:-200% center;}
    100%{background-position: 200% center;}
  }
  @keyframes gradMove{
    0%  {background-position:0% 50%;}
    50% {background-position:100% 50%;}
    100%{background-position:0% 50%;}
  }

  /* ── nav links ── */
  .af-link{
    color:#6b6880;text-decoration:none;font-size:11px;
    letter-spacing:0.07em;font-family:'Plus Jakarta Sans',sans-serif;
    font-weight:400;transition:color 0.2s;cursor:pointer;display:block;
  }
  .af-link:hover{color:#e8e8e8!important;}

  /* ── social icons ── */
  .af-social{
    width:36px;height:36px;border:1px solid #231e35;
    display:flex;align-items:center;justify-content:center;
    color:#6b6880;text-decoration:none;flex-shrink:0;
    transition:border-color 0.22s,color 0.22s,transform 0.22s;
  }
  .af-social:hover{
    border-color:transparent!important;
    background:linear-gradient(#07050f,#07050f) padding-box,
               linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4) border-box!important;
    border:1px solid transparent!important;
    color:#a855f7!important;
    transform:translateY(-2px);
  }

  /* ── app store buttons ── */
  .af-store-btn{
    display:flex;align-items:center;gap:10px;
    background:#120f20;border:1px solid #231e35;
    padding:9px 16px;cursor:pointer;text-decoration:none;
    border-radius:10px;
    transition:border-color 0.2s;flex-shrink:0;
  }
  .af-store-btn:hover{border-color:#6A00FF!important;}

  /* ── trustpilot review btn ── */
  .af-tp-review{
    display:inline-flex;align-items:center;gap:6px;
    padding:8px 16px;background:transparent;
    border:1px solid #00b67a;color:#00b67a;border-radius:8px;
    font-size:10px;font-weight:600;letter-spacing:0.14em;
    text-decoration:none;font-family:'Plus Jakarta Sans',sans-serif;
    cursor:pointer;transition:background 0.2s;
  }
  .af-tp-review:hover{background:rgba(0,182,122,0.08)!important;}

  /* ── become a seller — animated gradient pill ── */
  .af-btn-host{
    position:relative;display:inline-flex;align-items:center;gap:9px;
    color:#fff;border:none;border-radius:100px;
    padding:14px 30px;
    font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:800;
    cursor:pointer;overflow:hidden;z-index:0;
    transition:transform 0.18s,box-shadow 0.2s;
    white-space:nowrap;text-decoration:none;letter-spacing:0.02em;
    background:linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4);
    background-size:200% 100%;animation:gradMove 3s ease infinite;
  }
  .af-btn-host::before{
    content:'';position:absolute;inset:-2px;border-radius:102px;
    background:linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4,#6A00FF,#007BFF);
    background-size:300% 100%;animation:mrSpin 3s linear infinite;z-index:-1;
  }
  .af-btn-host::after{
    content:'';position:absolute;inset:2px;border-radius:98px;
    background:linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4);
    background-size:200% 100%;animation:gradMove 3s ease infinite;z-index:-1;
  }
  .af-btn-host:hover{transform:translateY(-3px);box-shadow:0 12px 32px rgba(106,0,255,0.5);}

  /* ── partner ghost pill ── */
  .af-btn-cohost{
    display:inline-flex;align-items:center;gap:9px;
    background:transparent;color:#e8e8e8;
    border:1px solid #231e35;border-radius:100px;
    padding:13px 28px;
    font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:700;
    cursor:pointer;transition:border-color 0.2s,color 0.2s,transform 0.18s;
    white-space:nowrap;text-decoration:none;letter-spacing:0.02em;
  }
  .af-btn-cohost:hover{border-color:#6A00FF;color:#a78bfa;transform:translateY(-2px);}

  /* ── shimmer on brand wordmark ── */
  .af-shimmer{
    background:linear-gradient(90deg,#007BFF 0%,#e8e8e8 30%,#6A00FF 55%,#FF00D4 75%,#007BFF 100%);
    background-size:200% auto;
    -webkit-background-clip:text;-webkit-text-fill-color:transparent;
    background-clip:text;
    animation:shimmer 4s linear infinite;
  }

  /* ── gradient text utility ── */
  .grad-text{
    background:linear-gradient(90deg,#007BFF 0%,#6A00FF 50%,#FF00D4 100%);
    -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  }

  /* ════════ DESKTOP layout ════════ */
  .af-header-inner{
    max-width:1100px;margin:0 auto;
    padding:56px 48px 52px;
    display:flex;flex-direction:row;align-items:center;
    justify-content:space-between;gap:40px;flex-wrap:wrap;
  }
  .af-wrap{
    max-width:1100px;margin:0 auto;
    padding:48px 48px 28px;
    display:flex;flex-direction:column;gap:40px;
  }
  .af-link-grid{
    display:grid;grid-template-columns:repeat(4,1fr);gap:32px 24px;
  }
  .af-brand-row{display:flex;align-items:flex-start;justify-content:space-between;gap:28px;flex-wrap:wrap;}
  .af-app-row{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:20px;}
  .af-app-btns{display:flex;gap:10px;}
  .af-tp-inner{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:20px;}
  .af-social-row{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:20px;}
  .af-bottom{display:flex;flex-wrap:wrap;align-items:center;gap:12px;}
  .af-cta-row{display:flex;gap:14px;flex-wrap:wrap;align-items:center;}

  /* ════════ MOBILE ════════ */
  @media(max-width:640px){
    .af-header-inner{
      flex-direction:row!important;align-items:flex-start!important;
      text-align:left!important;padding:28px 16px 24px!important;
      gap:16px!important;flex-wrap:nowrap!important;
    }
    .af-header-left{width:100%!important;align-items:flex-start!important;text-align:left!important;}
    .af-logo-block{justify-content:flex-start!important;width:100%!important;}
    .af-cta-row{flex-direction:row!important;width:100%!important;gap:8px!important;flex-wrap:wrap!important;}
    .af-btn-host,.af-btn-cohost{flex:1 1 auto!important;justify-content:center!important;font-size:11px!important;padding:11px 12px!important;}
    .af-trust-row{justify-content:flex-start!important;gap:10px!important;}
    .af-stripe-img{display:none!important;}
    .af-wrap{padding:28px 16px 20px!important;gap:28px!important;}
    .af-brand-row{flex-direction:row!important;flex-wrap:wrap!important;gap:12px!important;}
    .af-link-grid{grid-template-columns:1fr 1fr!important;gap:24px 20px!important;}
    .af-app-row{flex-direction:column!important;align-items:flex-start!important;gap:16px!important;}
    .af-app-btns{flex-direction:row!important;width:100%!important;gap:8px!important;}
    .af-store-btn{flex:1 1 auto!important;justify-content:center!important;}
    .af-tp-inner{flex-direction:row!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;flex-wrap:wrap!important;}
    .af-social-row{flex-direction:row!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;flex-wrap:wrap!important;}
    .af-bottom{gap:8px!important;flex-direction:row!important;align-items:center!important;flex-wrap:wrap!important;}
  }
`;

const currentYear = new Date().getFullYear();

/* ─── Trustpilot Stars ─── */
const TPStars: React.FC<{ rating?: number }> = ({ rating = 4.7 }) => (
  <div style={{ display:"flex", alignItems:"center", gap:3 }}>
    {[1,2,3,4,5].map(n => (
      <div key={n} style={{
        width:20, height:20,
        background: n <= Math.round(rating) ? "#00b67a" : "#1a1626",
        display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        <Star size={11} color="#fff" fill="#fff" strokeWidth={0}/>
      </div>
    ))}
  </div>
);

/* ─── Metarelm Logo with animated gradient ring ─── */
const MrLogo: React.FC<{ size?: number }> = ({ size = 56 }) => (
  <div style={{ position:"relative", display:"inline-flex", flexShrink:0 }}>
    <div style={{
      position:"absolute", inset:-3,
      borderRadius: size * 0.24 + 5,
      background: MR_G_ANIM,
      backgroundSize:"300% 100%",
      animation:"mrSpin 3s linear infinite",
      zIndex:0,
    }}/>
    <div style={{
      position:"relative", zIndex:1,
      background:"#07050f",
      borderRadius: size * 0.24 + 3,
      padding:3,
      display:"inline-flex",
    }}>
      <img
        src={logoImg}
        alt="Metarelm"
        style={{
          width:size, height:size,
          borderRadius: size * 0.22,
          objectFit:"cover",
          display:"block",
        }}
      />
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const SmartFooter: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const user       = useSelector((state: RootState) => state.auth.user);
  const { social } = useSelector((state: RootState) => state.social);

  useEffect(() => { dispatch(fetchSocialLinks()); }, [dispatch]);

  const buildSocialLink = (platform: string, handle?: string) => {
    if (!handle) return `https://${platform}.com`;
    if (handle.startsWith("http")) return handle;
    if (platform === "youtube") return `https://www.youtube.com/@${handle}`;
    return `https://${platform}.com/${handle}`;
  };

  const socialIcons = [
    { icon:<Facebook  size={15} strokeWidth={1.5}/>, href:buildSocialLink("facebook",  social?.facebook),  label:"Facebook"  },
    { icon:<Instagram size={15} strokeWidth={1.5}/>, href:buildSocialLink("instagram", social?.instagram), label:"Instagram" },
    { icon:<Twitter   size={15} strokeWidth={1.5}/>, href:buildSocialLink("twitter",   social?.twitter),   label:"Twitter"   },
    { icon:<Youtube   size={15} strokeWidth={1.5}/>, href:buildSocialLink("youtube",   social?.youtube),   label:"YouTube"   },
  ];

  const NavLink: React.FC<{ label:string; to:string }> = ({ label, to }) =>
    to.startsWith("/") || to.startsWith("http") === false ? (
      <span className="af-link" onClick={() => navigate(to)}>{label}</span>
    ) : (
      <a className="af-link" href={to} target="_blank" rel="noopener noreferrer">{label}</a>
    );

  /* Navigate to seller onboarding, or auth if not logged in */
  const handleBecomeSeller = () => {
    if (user?.userId) navigate(`/commerceonboarding/${user.userId}`);
    else navigate("/authswitch");
  };

  /* Navigate to partner onboarding, or auth if not logged in */
  const handlePartner = () => {
    if (user?.userId) navigate(`/partneronboarding/${user.userId}`);
    else navigate("/authswitch");
  };

  return (
    <footer style={{ background:C.bg, fontFamily:C.sans, color:C.offwhite }}>
      <style>{FONT_CSS}</style>

      {/* ══ HEADER SECTION ══ */}
      <section style={{
        background:"#0a0714",
        borderBottom:`1px solid ${C.border}`,
        position:"relative",
        overflow:"hidden",
      }}>
        {/* Blue/purple radial glow — left */}
        <div style={{
          position:"absolute", top:-80, left:"30%", transform:"translateX(-50%)",
          width:500, height:400, pointerEvents:"none",
          background:"radial-gradient(ellipse, rgba(106,0,255,0.12) 0%, transparent 68%)",
        }}/>
        {/* Pink radial glow — right */}
        <div style={{
          position:"absolute", top:-60, right:"10%",
          width:380, height:320, pointerEvents:"none",
          background:"radial-gradient(ellipse, rgba(255,0,212,0.08) 0%, transparent 68%)",
        }}/>
        {/* Gradient accent line */}
        <div style={{ height:1, background:`linear-gradient(90deg,transparent,#6A00FF,#FF00D4,transparent)` }}/>

        <div className="af-header-inner">
          <div className="af-header-left" style={{ display:"flex", flexDirection:"column", gap:22, flex:1, minWidth:0 }}>

            {/* Logo + wordmark */}
            <div className="af-logo-block" style={{ display:"flex", alignItems:"center", gap:16, width:"100%" }}>
              <MrLogo size={56} />
              <div>
                <div className="af-shimmer" style={{
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                  fontSize:28, fontWeight:900,
                  letterSpacing:"-0.02em", lineHeight:1,
                }}>
                  METARELM
                </div>
                <p style={{
                  fontSize:8, color:C.muted,
                  letterSpacing:"0.26em", marginTop:7,
                  fontWeight:400, fontFamily:C.sans,
                }}>
                  ELECTRONICS · MARKETPLACE · GLOBAL
                </p>
              </div>
            </div>

            {/* Tagline */}
            <p style={{ fontSize:9, letterSpacing:"0.24em", marginTop:-8, fontWeight:400 }}>
              <span className="grad-text">WHERE TECH MEETS THE WORLD</span>
            </p>

            {/* Location / reach badges */}
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              {[
                [<MapPin size={9} strokeWidth={1.5}/>, "Tallinn, Estonia · EU Regulated"],
                [<Globe  size={9} strokeWidth={1.5}/>, "Operating in 100+ countries"],
              ].map(([icon, text], i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:7 }}>
                  <span style={{ color:C.muted, display:"flex" }}>{icon as React.ReactNode}</span>
                  <span style={{ fontSize:9, color:C.muted, letterSpacing:"0.1em" }}>{text as string}</span>
                </div>
              ))}
            </div>

            {/* Headline */}
            <div>
              <h2 style={{
                fontFamily:"'Plus Jakarta Sans',sans-serif",
                fontSize:30, fontWeight:800, color:C.white,
                lineHeight:1.3, letterSpacing:"-0.02em",
                margin:"0 0 10px",
              }}>
                Your products.<br/>Your reach. Your terms.
              </h2>
              <p style={{
                fontSize:12, color:C.muted,
                lineHeight:1.85, letterSpacing:"0.04em",
                maxWidth:440,
              }}>
                Join thousands of sellers already growing on Metarelm across 100+ countries.
                List in minutes. Get paid instantly.
              </p>
            </div>

            {/* CTA buttons */}
            <div className="af-cta-row">
              <button className="af-btn-host" onClick={handleBecomeSeller}>
                <ShoppingBag size={14}/> Become a Seller <ArrowRight size={13}/>
              </button>
              <button className="af-btn-cohost" onClick={handlePartner}>
                Find a Partner <ArrowRight size={13}/>
              </button>
            </div>

            {/* Trust badges */}
            <div className="af-trust-row" style={{ display:"flex", alignItems:"center", gap:20, flexWrap:"wrap" }}>
              {["Instant Payouts","Verified Buyers","EU Regulated","24/7 Support"].map(label => (
                <div key={label} style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span className="grad-text" style={{ fontSize:10, fontWeight:800 }}>✓</span>
                  <span style={{ fontSize:9, color:C.muted, letterSpacing:"0.1em" }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GRADIENT TOP ACCENT */}
      <div style={{ height:1, background:"linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4, transparent)" }}/>

      {/* ══ BODY ══ */}
      <div className="af-wrap">

        {/* BRAND ROW */}
        <div className="af-brand-row">
          <div>
            <div className="af-logo-block" style={{ display:"flex", alignItems:"center", gap:16, width:"100%" }}>
              <MrLogo size={56} />
              <div>
                <div className="af-shimmer" style={{
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                  fontSize:28, fontWeight:900,
                  letterSpacing:"-0.02em", lineHeight:1,
                }}>
                  METARELM
                </div>
                <p style={{
                  fontSize:8, color:C.muted,
                  letterSpacing:"0.26em", marginTop:7,
                  fontWeight:400,
                }}>
                  ELECTRONICS · MARKETPLACE · GLOBAL
                </p>
              </div>
            </div>
            <p style={{ fontSize:9, letterSpacing:"0.24em", marginTop:8, fontWeight:400 }}>
              <span className="grad-text">WHERE TECH MEETS THE WORLD</span>
            </p>
            <div style={{ display:"flex", flexDirection:"column", gap:5, marginTop:12 }}>
              {[
                [<MapPin size={9} strokeWidth={1.5}/>, "Tallinn, Estonia · EU Regulated"],
                [<Globe  size={9} strokeWidth={1.5}/>, "Operating in 100+ countries"],
              ].map(([icon, text], i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:7 }}>
                  <span style={{ color:C.muted, display:"flex" }}>{icon as React.ReactNode}</span>
                  <span style={{ fontSize:9, color:C.muted, letterSpacing:"0.1em" }}>{text as string}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RULE */}
        <div style={{ height:1, background:C.border }}/>

        {/* LINK COLUMNS */}
        <div className="af-link-grid">

          {/* Company links */}
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <p style={S.colHead}>COMPANY</p>
            {[
              ["About Us",   "https://metarelm.com/pages/about-us"],
              ["Careers",    "https://metarelm.com/pages/careers"],
              ["Newsroom",   "https://metarelm.com/pages/newsroom"],
              ["Investors",  "https://metarelm.com/pages/investors"],
              ["Gift Cards", "https://metarelm.com/pages/gift-cards"],
              ["Blog",       "/News"],
            ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
          </div>

          {/* Selling links */}
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <p style={S.colHead}>SELLING</p>
            <span className="af-link" onClick={handleBecomeSeller}>Become a Seller</span>
            {[
              ["How To Onboard", "/HostOnboardHelp"],
              ["Payouts Policy", "/PayoutsPolicy"],
              ["Order Policy",   "/CancellationPolicy"],
            ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
            <span className="af-link" onClick={handlePartner}>Find a Partner</span>
          </div>

          {/* Support links */}
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <p style={S.colHead}>SUPPORT</p>
            {[
              ["Help Center",  "https://metarelm.com/pages/helpsupport"],
              ["Contact Us",   "/contact-us"],
              ["Order Policy", "/CancellationPolicy"],
              ["Safety Info",  "https://metarelm.com/pages/safety"],
              ["Disability",   "https://metarelm.com/pages/disability-support"],
            ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
          </div>

          {/* Legal links */}
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <p style={S.colHead}>LEGAL</p>
            {[
              ["Terms of Service",     "https://metarelm.com/pages/terms"],
              ["Privacy Policy",       "https://metarelm.com/pages/privacy-settings"],
              ["Your Privacy Choices", "https://metarelm.com/pages/your-privacy-choices"],
              ["Legal",                "https://metarelm.com/pages/legal"],
              ["Sitemap",              "https://metarelm.com/sitemap.xml"],
            ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
          </div>

        </div>

        {/* RULE */}
        <div style={{ height:1, background:C.border }}/>

        {/* APP DOWNLOAD */}
        <div className="af-app-row">
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <Smartphone size={18} strokeWidth={1.5} style={{ color:"transparent", background: MR_G, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" } as React.CSSProperties}/>
            <div>
              <p style={{ fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:C.white, margin:0 }}>
                DOWNLOAD THE METARELM APP
              </p>
              <p style={{ fontSize:9, color:C.muted, marginTop:4, letterSpacing:"0.08em" }}>
                Available on iOS and Android
              </p>
            </div>
          </div>
          <div className="af-app-btns">
            <a href="https://play.google.com/store/apps/details?id=com.metarelm.android"
              target="_blank" rel="noopener noreferrer"
              className="af-store-btn">
              <img src={GoogleLogo} alt="Google Play" style={{ height:22, objectFit:"contain" }}/>
              <div>
                <div style={{ fontSize:7, color:C.muted, letterSpacing:"0.1em" }}>GET IT ON</div>
                <div style={{ fontSize:11, color:C.offwhite, fontWeight:600, letterSpacing:"0.04em" }}>Google Play</div>
              </div>
            </a>
            <a href="https://apps.apple.com/app/idYOUR_APP_ID"
              target="_blank" rel="noopener noreferrer"
              className="af-store-btn">
              <img src={AppleLogo} alt="App Store"
                style={{ height:22, objectFit:"contain", filter:"brightness(0) invert(1)" }}/>
              <div>
                <div style={{ fontSize:7, color:C.muted, letterSpacing:"0.1em" }}>DOWNLOAD ON THE</div>
                <div style={{ fontSize:11, color:C.offwhite, fontWeight:600, letterSpacing:"0.04em" }}>App Store</div>
              </div>
            </a>
          </div>
        </div>

        {/* TRUSTPILOT */}
        <div style={{ border:`1px solid ${C.border2}`, background:C.surface, padding:"20px 22px", borderRadius:12 }}>
          <div className="af-tp-inner">
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:18, height:18, background:"#00b67a", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Star size={10} color="#fff" fill="#fff" strokeWidth={0}/>
                </div>
                <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:14, fontWeight:700, color:C.white, letterSpacing:"-0.01em" }}>
                  Trustpilot
                </span>
              </div>
              <TPStars rating={4.7} />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, alignItems:"flex-end" }}>
              <a href="https://www.trustpilot.com/review/metarelm.com"
                target="_blank" rel="noopener noreferrer"
                className="af-tp-review">
                <Star size={11} color="#00b67a" strokeWidth={1.5}/>
                WRITE US A REVIEW
              </a>
              <span style={{ fontSize:9, color:C.muted, letterSpacing:"0.08em" }}>Verified by Trustpilot</span>
              <div style={{ display:"flex", gap:2 }}>
                {[1,2,3,4,5].map(n => (
                  <Star key={n} size={10} color={n<=4?"#00b67a":"#231e35"} fill={n<=4?"#00b67a":"transparent"} strokeWidth={n<=4?0:1.5}/>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* STRIPE PAYMENT BADGE */}
        <img
          src={StripeImage}
          alt="Secure payments powered by Stripe"
          className="af-stripe-img"
          style={{ height:56, objectFit:"contain", filter:"brightness(0) invert(1)", opacity:0.35, flexShrink:0 }}
        />

        {/* RULE */}
        <div style={{ height:1, background:C.border }}/>

        {/* SOCIALS */}
        <div className="af-social-row">
          <div>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:C.white, margin:0 }}>
              CONNECT WITH US
            </p>
            <p style={{ fontSize:9, color:C.muted, marginTop:5, letterSpacing:"0.08em" }}>
              Share your Metarelm experience
            </p>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {socialIcons.map(({ icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                className="af-social" title={label}>
                {icon}
              </a>
            ))}
          </div>
        </div>

        {/* RULE */}
        <div style={{ height:1, background:C.border }}/>

        {/* BOTTOM LEGAL ROW */}
        <div className="af-bottom">
          <div style={{ display:"flex", alignItems:"center", gap:5, flexShrink:0 }}>
            <Copyright size={10} color={C.muted} strokeWidth={1.5}/>
            <span style={{ fontSize:9, color:C.muted, letterSpacing:"0.08em" }}>
              2025 – {currentYear} Metarelm, Inc.
            </span>
          </div>
          <span style={{ color:C.border2, fontSize:10 }}>·</span>
          {[
            ["About Us",        "https://metarelm.com/pages/about-us"],
            ["Terms",           "https://metarelm.com/pages/terms"],
            ["Privacy",         "https://metarelm.com/pages/privacy-settings"],
            ["Legal",           "https://metarelm.com/pages/legal"],
            ["News",            "/News"],
            ["Sitemap",         "https://metarelm.com/sitemap.xml"],
            ["Become a Seller", "/authswitch"],
            ["Contact Us",      "/contact-us"],
          ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
        </div>

      </div>{/* end af-wrap */}

      {/* GRADIENT BOTTOM ACCENT */}
      <div style={{ height:1, background:"linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4, transparent)" }}/>
    </footer>
  );
};

export default SmartFooter;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  colHead: {
    fontSize: 8, fontWeight: 700, letterSpacing: "0.28em",
    marginBottom: 4, fontFamily: "'Plus Jakarta Sans',sans-serif",
    background: "linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4)",
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
  },
};